import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';

import { useTranslate } from 'src/locales';

import Iconify from 'src/components/iconify';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

export interface DataListAction {
  key: string;
  label: string;
  icon?: string;
  onClick: () => void;
  /** Rendered but non-interactive (e.g. bulk actions with no rows selected). */
  disabled?: boolean;
  /** Omitted entirely (e.g. tenant-gated actions). */
  hidden?: boolean;
  color?: string;
  /** A second line under the label, e.g. why the action is disabled. */
  caption?: string;
  /** Draws a divider above this action, to separate list-level actions from row actions. */
  divider?: boolean;
}

/**
 * The consolidated "Actions ▾" toolbar dropdown for the shared lists (JT1-1528). Restores the single
 * menu the old lists had (create / import / invite / bulk actions) rather than a row of separate
 * buttons, and keeps candidates/contacts/companies consistent. Bulk actions pass `disabled` so they
 * show but are inert until rows are selected.
 */
export default function DataListActionsMenu({ actions, label }: { actions: DataListAction[]; label?: string }) {
  const { t } = useTranslate();
  const popover = usePopover();

  const visible = actions.filter((a) => !a.hidden);
  if (!visible.length) return null;

  return (
    <>
      <Button
        variant="contained"
        size="small"
        endIcon={<Iconify icon="eva:chevron-down-fill" />}
        onClick={popover.onOpen}
      >
        {label ?? t('common.actions', { defaultValue: 'Actions' })}
      </Button>

      <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ minWidth: 200 }}>
        {visible.flatMap((action, index) => [
          ...(action.divider && index > 0 ? [<Divider key={`${action.key}-divider`} sx={{ borderStyle: 'dashed' }} />] : []),
          <MenuItem
            key={action.key}
            disabled={action.disabled}
            sx={action.color ? { color: action.color } : undefined}
            onClick={() => {
              popover.onClose();
              action.onClick();
            }}
          >
            {action.icon && <Iconify icon={action.icon} />}
            {action.caption ? (
              <Stack>
                <Typography variant="body2">{action.label}</Typography>
                <Typography variant="caption" color="text.secondary">{action.caption}</Typography>
              </Stack>
            ) : (
              <Typography variant="body2">{action.label}</Typography>
            )}
          </MenuItem>,
        ])}
      </CustomPopover>
    </>
  );
}
